import { toPackageType } from "@b2bportal/core-packages";
import { arraysAreEqual } from "@b2bportal/core-utilities";
import type { PackageSearchQueryParams } from "@tenant/types";
import type { PackagesSearch } from "@/store/packages/types/packagesSearch";

/**
 * True when the search in the URL is the one already held in
 * `requestedSearch`, i.e. no initializePackages is needed for it.
 */
export const isSameRequestedSearch = (
  search: Pick<
    PackageSearchQueryParams,
    | "origin"
    | "destination"
    | "fromDate"
    | "untilDate"
    | "adults"
    | "children"
    | "infantsOnLap"
    | "infantsInSeat"
    | "roomCount"
    | "driverAge"
  > & {
    // Loosely typed: core's query params carry it as a plain string.
    packageType?: string | null;
  },
  requestedSearch: PackagesSearch | null | undefined
): boolean => {
  if (requestedSearch == null) {
    return false;
  }

  const {
    origin: encodedOrigin,
    destination: encodedDestination,
    fromDate,
    untilDate,
    adults,
    children,
    infantsOnLap,
    infantsInSeat,
    roomCount,
    driverAge,
    packageType,
  } = search;

  const origin = decodeURIComponent(encodedOrigin);
  const destination = decodeURIComponent(encodedDestination);

  const isOriginSame = origin === requestedSearch.origin?.label;
  const isDestinationSame = destination === requestedSearch.destination?.label;

  const isFromDateSame = fromDate === requestedSearch.dateRange.from;
  const isUntilDateSame = untilDate === requestedSearch.dateRange.until;
  const isChildrenSame =
    children != null
      ? arraysAreEqual(children, requestedSearch.guests.childAges)
      : requestedSearch.guests.childAges.length === 0;
  const isAdultsSame = adults === requestedSearch.guests.adultCount;
  const isRoomCountSame = roomCount === requestedSearch.roomCount;
  const isInfantsOnLapSame =
    (infantsOnLap ?? 0) === requestedSearch.guests.infantOnLapAges.length;
  const isInfantsInSeatSame =
    (infantsInSeat ?? 0) === requestedSearch.guests.infantInSeatAges.length;
  const isDriverAgeSame =
    (driverAge ?? 25) === (requestedSearch.driverAge ?? 25);
  // Same defaulting as the URL parser, so a missing package_type param
  // doesn't read as a different search.
  const isPackageTypeSame =
    toPackageType(packageType ?? null) ===
    toPackageType(requestedSearch.packageType ?? null);

  return (
    isOriginSame &&
    isDestinationSame &&
    isFromDateSame &&
    isUntilDateSame &&
    isChildrenSame &&
    isAdultsSame &&
    isRoomCountSame &&
    isInfantsOnLapSame &&
    isInfantsInSeatSame &&
    isDriverAgeSame &&
    isPackageTypeSame
  );
};
